/**
 * settingsStorage.js — Adapted for RaceProX
 *
 * Persistence is handled via Supabase (dataLoader.js) instead of localStorage.
 * The createPresetSnapshot and parseStoredPreset functions remain the same
 * to maintain compatibility with the main.js engine state.
 *
 * The save/load/export/import functions are adapted:
 * - savePresetToStorage → uploads assets to Storage, then saves to Supabase
 * - loadPresetFromStorage → returns null (data comes from bootstrap)
 * - clearPresetStorage → no-op
 * - Export/import still work via JSON file download/upload
 */
import { pickSettings, sanitizeSettings } from './settings.js';
import { savePreset, uploadHologramAsset, storedPresetToDbPayload } from './dataLoader.js';

export const PRESET_STORAGE_KEY = 'racing-track-hologram:preset';
export const PRESET_EXPORT_BASENAME = 'hologram-preset';
export const PRESET_VERSION = 5;
const LEGACY_PRESET_VERSION = 1;

/**
 * @typedef {{
 *   id: string;
 *   firstName: string;
 *   lastName: string;
 *   profileImage: string;
 *   numberImage: string;
 * }} StoredDriver
 */

/**
 * @typedef {{
 *   version: number;
 *   settings: Partial<import('./settings.js').HologramSettings>;
 *   customCornerTs?: number[];
 *   excludedCornerTs?: number[];
 *   cornerNames?: Record<string, string>;
 *   markerDrivers?: Record<string, { driverId: string; quote: string }>;
 *   drivers?: StoredDriver[];
 *   svgSource?: string | null;
 *   svgFileLabel?: string;
 *   mapDataUrl?: string | null;
 *   mapFileLabel?: string | null;
 *   overlayFontDataUrl?: string | null;
 *   overlayFontFileLabel?: string | null;
 *   venueTitleFontDataUrl?: string | null;
 *   venueTitleFontFileLabel?: string | null;
 *   flagTextureDataUrl?: string | null;
 *   flagTextureFileLabel?: string | null;
 *   venueHeaderFlagDataUrl?: string | null;
 *   venueHeaderFlagFileLabel?: string | null;
 *   venueCircuitId?: string | null;
 *   startMarkerT?: number | null;
 *   svgStoragePath?: string | null;
 *   mapStoragePath?: string | null;
 *   displayFontStoragePath?: string | null;
 * }} StoredPreset
 */

/**
 * @param {unknown} value
 * @returns {value is StoredDriver}
 */
function isStoredDriver(value) {
  if (!value || typeof value !== 'object') return false;
  const d = /** @type {Record<string, unknown>} */ (value);
  const imageOkOrOmitted = (src) =>
    src == null ||
    src === '' ||
    (typeof src === 'string' && (src.startsWith('data:') || src.startsWith('/') || src.startsWith('http')));
  return (
    typeof d.id === 'string' &&
    d.id.trim() &&
    typeof d.firstName === 'string' &&
    typeof d.lastName === 'string' &&
    imageOkOrOmitted(d.profileImage) &&
    imageOkOrOmitted(d.numberImage)
  );
}

/**
 * @param {{
 *   settings: import('./settings.js').HologramSettings;
 *   customCornerTs: number[];
 *   excludedCornerTs: number[];
 *   cornerNames: Map<string, string>;
 *   markerDrivers: Map<string, { driverId: string; quote: string }>;
 *   drivers: import('./drivers.js').Driver[];
 *   svgSource: string | null;
 *   svgFileLabel: string;
 *   mapDataUrl: string | null;
 *   mapFileLabel: string | null;
 *   overlayFontDataUrl?: string | null;
 *   overlayFontFileLabel?: string | null;
 *   venueTitleFontDataUrl?: string | null;
 *   venueTitleFontFileLabel?: string | null;
 *   flagTextureDataUrl?: string | null;
 *   flagTextureFileLabel?: string | null;
 *   venueHeaderFlagDataUrl?: string | null;
 *   venueHeaderFlagFileLabel?: string | null;
 *   venueCircuitId?: string | null;
 *   startMarkerT?: number | null;
 * }} snapshot
 * @returns {StoredPreset}
 */
export function createPresetSnapshot({
  settings,
  customCornerTs,
  excludedCornerTs,
  cornerNames,
  markerDrivers,
  drivers,
  svgSource,
  svgFileLabel,
  mapDataUrl,
  mapFileLabel,
  overlayFontDataUrl = null,
  overlayFontFileLabel = null,
  venueTitleFontDataUrl = null,
  venueTitleFontFileLabel = null,
  flagTextureDataUrl = null,
  flagTextureFileLabel = null,
  venueHeaderFlagDataUrl = null,
  venueHeaderFlagFileLabel = null,
  venueCircuitId = null,
  startMarkerT = null,
}) {
  return {
    version: PRESET_VERSION,
    settings: pickSettings(settings),
    customCornerTs: customCornerTs.filter((t) => Number.isFinite(t)),
    excludedCornerTs: excludedCornerTs.filter((t) => Number.isFinite(t)),
    startMarkerT:
      startMarkerT != null && Number.isFinite(startMarkerT) ? startMarkerT : null,
    cornerNames: Object.fromEntries(cornerNames),
    markerDrivers: Object.fromEntries(
      Array.from(markerDrivers.entries()).map(([k, v]) => [k, { ...v }])
    ),
    drivers: drivers.map((d) => ({
      id: d.id,
      firstName: d.firstName,
      lastName: d.lastName,
      profileImage: d.profileImage,
      numberImage: d.numberImage,
    })),
    svgSource: svgSource ?? null,
    svgFileLabel: svgFileLabel || 'Circuit SVG',
    mapDataUrl: mapDataUrl ?? null,
    mapFileLabel: mapFileLabel ?? null,
    overlayFontDataUrl: overlayFontDataUrl ?? null,
    overlayFontFileLabel: overlayFontFileLabel ?? null,
    venueTitleFontDataUrl: venueTitleFontDataUrl ?? null,
    venueTitleFontFileLabel: venueTitleFontFileLabel ?? null,
    flagTextureDataUrl: flagTextureDataUrl ?? null,
    flagTextureFileLabel: flagTextureFileLabel ?? null,
    venueHeaderFlagDataUrl: venueHeaderFlagDataUrl ?? null,
    venueHeaderFlagFileLabel: venueHeaderFlagFileLabel ?? null,
    venueCircuitId: venueCircuitId ?? null,
  };
}

/**
 * @param {unknown} value
 * @returns {StoredPreset | null}
 */
export function parseStoredPreset(value) {
  if (!value || typeof value !== 'object') return null;
  const record = /** @type {Record<string, unknown>} */ (value);
  const version = record.version;
  if (
    version !== PRESET_VERSION &&
    version !== 4 &&
    version !== 3 &&
    version !== 2 &&
    version !== LEGACY_PRESET_VERSION
  ) {
    return null;
  }

  const settings = sanitizeSettings(record.settings);

  /** @type {StoredPreset} */
  const preset = {
    version: PRESET_VERSION,
    settings,
  };

  if (Array.isArray(record.customCornerTs)) {
    preset.customCornerTs = record.customCornerTs
      .map((t) => Number(t))
      .filter((t) => Number.isFinite(t) && t >= 0 && t <= 1);
  }

  if (Array.isArray(record.excludedCornerTs)) {
    preset.excludedCornerTs = record.excludedCornerTs
      .map((t) => Number(t))
      .filter((t) => Number.isFinite(t) && t >= 0 && t <= 1);
  }

  if (record.cornerNames && typeof record.cornerNames === 'object') {
    preset.cornerNames = {};
    for (const [key, name] of Object.entries(
      /** @type {Record<string, unknown>} */ (record.cornerNames),
    )) {
      if (typeof name === 'string' && name.trim()) {
        preset.cornerNames[key] = name.trim();
      }
    }
  }

  // F8: Unified markerDrivers — new format
  if (record.markerDrivers && typeof record.markerDrivers === 'object') {
    preset.markerDrivers = {};
    for (const [key, val] of Object.entries(
      /** @type {Record<string, unknown>} */ (record.markerDrivers),
    )) {
      if (val && typeof val === 'object') {
        const entry = /** @type {Record<string, unknown>} */ (val);
        preset.markerDrivers[key] = {
          driverId: String(entry.driverId || ''),
          quote: String(entry.quote || ''),
        };
      }
    }
  }
  // Legacy format (old JSON exports with cornerDriverIds + cornerDescriptions)
  else if (record.cornerDriverIds && typeof record.cornerDriverIds === 'object') {
    preset.markerDrivers = {};
    const descriptions = /** @type {Record<string, unknown>} */ (
      record.cornerDescriptions && typeof record.cornerDescriptions === 'object'
        ? record.cornerDescriptions
        : {}
    );
    for (const [key, id] of Object.entries(
      /** @type {Record<string, unknown>} */ (record.cornerDriverIds),
    )) {
      if (typeof id === 'string' && id.trim()) {
        preset.markerDrivers[key] = {
          driverId: id.trim(),
          quote: (typeof descriptions[key] === 'string' ? descriptions[key] : '').trim(),
        };
      }
    }
  }

  if (Array.isArray(record.drivers)) {
    preset.drivers = record.drivers.filter(isStoredDriver).map((d) => ({
      id: d.id.trim(),
      firstName: d.firstName.trim(),
      lastName: d.lastName.trim(),
      profileImage:
        typeof d.profileImage === 'string' && d.profileImage ? d.profileImage : '',
      numberImage:
        typeof d.numberImage === 'string' && d.numberImage ? d.numberImage : '',
    }));
  }

  if (record.svgSource === null) {
    preset.svgSource = null;
  } else if (typeof record.svgSource === 'string' && record.svgSource.trim()) {
    preset.svgSource = record.svgSource;
  }

  if (typeof record.svgFileLabel === 'string') {
    preset.svgFileLabel = record.svgFileLabel;
  }

  if (record.mapDataUrl === null) {
    preset.mapDataUrl = null;
  } else if (typeof record.mapDataUrl === 'string' && (record.mapDataUrl.startsWith('data:') || record.mapDataUrl.startsWith('https://'))) {
    preset.mapDataUrl = record.mapDataUrl;
  }

  if (typeof record.mapFileLabel === 'string') {
    preset.mapFileLabel = record.mapFileLabel;
  }

  if (record.overlayFontDataUrl === null) {
    preset.overlayFontDataUrl = null;
  } else if (
    typeof record.overlayFontDataUrl === 'string' &&
    (record.overlayFontDataUrl.startsWith('data:') || record.overlayFontDataUrl.startsWith('https://'))
  ) {
    preset.overlayFontDataUrl = record.overlayFontDataUrl;
  }

  if (typeof record.overlayFontFileLabel === 'string') {
    preset.overlayFontFileLabel = record.overlayFontFileLabel;
  }

  if (record.venueTitleFontDataUrl === null) {
    preset.venueTitleFontDataUrl = null;
  } else if (
    typeof record.venueTitleFontDataUrl === 'string' &&
    record.venueTitleFontDataUrl.startsWith('data:')
  ) {
    preset.venueTitleFontDataUrl = record.venueTitleFontDataUrl;
  }

  if (typeof record.venueTitleFontFileLabel === 'string') {
    preset.venueTitleFontFileLabel = record.venueTitleFontFileLabel;
  }

  if (record.flagTextureDataUrl === null) {
    preset.flagTextureDataUrl = null;
  } else if (
    typeof record.flagTextureDataUrl === 'string' &&
    record.flagTextureDataUrl.startsWith('data:')
  ) {
    preset.flagTextureDataUrl = record.flagTextureDataUrl;
  }

  if (typeof record.flagTextureFileLabel === 'string') {
    preset.flagTextureFileLabel = record.flagTextureFileLabel;
  }

  if (record.venueHeaderFlagDataUrl === null) {
    preset.venueHeaderFlagDataUrl = null;
  } else if (
    typeof record.venueHeaderFlagDataUrl === 'string' &&
    record.venueHeaderFlagDataUrl.startsWith('data:')
  ) {
    preset.venueHeaderFlagDataUrl = record.venueHeaderFlagDataUrl;
  }

  if (typeof record.venueHeaderFlagFileLabel === 'string') {
    preset.venueHeaderFlagFileLabel = record.venueHeaderFlagFileLabel;
  }

  if (typeof record.venueCircuitId === 'string' && record.venueCircuitId.trim()) {
    preset.venueCircuitId = record.venueCircuitId.trim();
  }

  if (record.startMarkerT === null) {
    preset.startMarkerT = null;
  } else if (record.startMarkerT !== undefined) {
    const t = Number(record.startMarkerT);
    if (Number.isFinite(t) && t >= 0 && t <= 1) {
      preset.startMarkerT = t;
    }
  }

  // v4 → v5: cornerTourTravel → arc-length travel system
  if (preset.settings.cornerTourTravel != null && preset.settings.cornerTourTravelSpeed == null) {
    const oldTravel = Number(preset.settings.cornerTourTravel) || 2.8;
    preset.settings.cornerTourTravelMultiplier = Math.max(0.5, Math.min(3, oldTravel / 2.8));
    preset.settings.cornerTourTravelSpeed = 12;
    preset.settings.cornerTourTravelMin = 0.8;
    preset.settings.cornerTourTravelMax = 20;
    delete preset.settings.cornerTourTravel;
  }

  // F3: Storage path fields
  if (typeof record.svgStoragePath === 'string' && record.svgStoragePath.trim()) {
    preset.svgStoragePath = record.svgStoragePath;
  }
  if (typeof record.mapStoragePath === 'string' && record.mapStoragePath.trim()) {
    preset.mapStoragePath = record.mapStoragePath;
  }
  if (typeof record.displayFontStoragePath === 'string' && record.displayFontStoragePath.trim()) {
    preset.displayFontStoragePath = record.displayFontStoragePath;
  }

  return preset;
}

// ─── Supabase Persistence (replaces localStorage) ──────────────────────

/** @type {{ orgId: string; presetId: string } | null} */
let _activeContext = null;

/**
 * Set the active preset context for Supabase save operations (F3).
 * Replaces setActivePresetId — now also stores orgId for Storage paths.
 * Called by main.js after bootstrap.
 * @param {string} orgId
 * @param {string} presetId
 */
export function setActivePresetContext(orgId, presetId) {
  _activeContext = { orgId, presetId };
}

/**
 * @returns {string | null}
 */
export function getActivePresetId() {
  return _activeContext?.presetId ?? null;
}

/**
 * Legacy compat — still exported for code that only needs to check/set the ID.
 * @param {string | null} id
 */
export function setActivePresetId(id) {
  if (id && _activeContext) {
    _activeContext.presetId = id;
  } else if (id) {
    // Fallback: context without orgId (won't be able to upload assets)
    _activeContext = { orgId: '', presetId: id };
  } else {
    _activeContext = null;
  }
}

// ─── Persistent storage path state (P1-1) ──────────────────────────────
// Tracks the paths currently stored in the DB so they survive round-trips.
// Without this, a font loaded as an HTTPS URL would be written back as null.

/** @type {{ svg: string | null; map: string | null; displayFont: string | null }} */
let _currentStoragePaths = { svg: null, map: null, displayFont: null };

/**
 * Update tracked storage paths — call after applying a preset from the DB.
 * @param {{ svgStoragePath?: string | null; mapStoragePath?: string | null; displayFontStoragePath?: string | null }} paths
 */
export function setCurrentStoragePaths(paths) {
  _currentStoragePaths = {
    svg: paths?.svgStoragePath ?? null,
    map: paths?.mapStoragePath ?? null,
    displayFont: paths?.displayFontStoragePath ?? null,
  };
}

/**
 * @returns {{ svg: string | null; map: string | null; displayFont: string | null }}
 */
export function getCurrentStoragePaths() {
  return { ..._currentStoragePaths };
}

/**
 * Save preset to Supabase with asset upload lifecycle (F3).
 * Uploads binary assets to Storage, then saves metadata to DB.
 *
 * P1-1: Always starts from the current DB paths so existing assets
 *       are never overwritten with null.
 * P1-2: Upload failures preserve the previous path and accumulate
 *       warnings returned to the caller.
 *
 * @param {StoredPreset} preset
 * @returns {Promise<{ warnings: string[] }>}
 */
export async function savePresetToStorage(preset) {
  if (!_activeContext) {
    console.warn('[settingsStorage] No active context — cannot save.');
    return { warnings: [] };
  }
  const { orgId, presetId } = _activeContext;

  // Start from current DB paths (P1-1) — never null unless genuinely cleared
  let svgStoragePath = preset.svgStoragePath ?? _currentStoragePaths.svg;
  let mapStoragePath = preset.mapStoragePath ?? _currentStoragePaths.map;
  let displayFontStoragePath = preset.displayFontStoragePath ?? _currentStoragePaths.displayFont;

  /** @type {string[]} */
  const warnings = [];

  // SVG: upload when fresh source exists
  if (preset.svgSource && orgId) {
    try {
      const blob = new Blob([preset.svgSource], { type: 'image/svg+xml' });
      svgStoragePath = await uploadHologramAsset(
        orgId, presetId, 'circuit.svg', blob, 'image/svg+xml'
      );
    } catch (err) {
      console.warn('[settingsStorage] SVG upload failed:', err);
      warnings.push('SVG upload failed — previous version kept');
      // svgStoragePath stays at the existing value (P1-2)
    }
  }

  // Map: upload when fresh data URL exists
  if (preset.mapDataUrl && preset.mapDataUrl.startsWith('data:') && orgId) {
    try {
      const resp = await fetch(preset.mapDataUrl);
      const blob = await resp.blob();
      const ext = blob.type.includes('webp') ? 'webp' : 'png';
      mapStoragePath = await uploadHologramAsset(
        orgId, presetId, `map.${ext}`, blob, blob.type
      );
    } catch (err) {
      console.warn('[settingsStorage] Map upload failed:', err);
      warnings.push('Map upload failed — previous version kept');
    }
  }

  // Font: upload when fresh data URL exists
  if (preset.overlayFontDataUrl && preset.overlayFontDataUrl.startsWith('data:') && orgId) {
    try {
      const resp = await fetch(preset.overlayFontDataUrl);
      const blob = await resp.blob();
      displayFontStoragePath = await uploadHologramAsset(
        orgId, presetId, 'display-font.woff2', blob, 'font/woff2'
      );
    } catch (err) {
      console.warn('[settingsStorage] Font upload failed:', err);
      warnings.push('Font upload failed — previous version kept');
    }
  }

  // Build DB payload with final storage paths
  const enrichedPreset = {
    ...preset,
    svgStoragePath,
    mapStoragePath,
    displayFontStoragePath,
  };
  const payload = storedPresetToDbPayload(enrichedPreset);

  // Save to DB
  await savePreset(presetId, payload);

  // Update tracked paths to match what we just wrote (P1-1)
  _currentStoragePaths = { svg: svgStoragePath, map: mapStoragePath, displayFont: displayFontStoragePath };

  return { warnings };
}

/**
 * Load preset from storage — returns null.
 * Data comes from the bootstrap layer (Supabase), not localStorage.
 * @returns {StoredPreset | null}
 */
export function loadPresetFromStorage() {
  return null;
}

export function clearPresetStorage() {
  // No-op — not using localStorage
}

export function hasSavedPreset() {
  return _activeContext?.presetId != null;
}

/**
 * @param {StoredPreset} preset
 * @returns {string}
 */
export function serializePreset(preset) {
  return JSON.stringify(preset, null, 2);
}

/**
 * @param {StoredPreset} preset
 * @param {string} [filename]
 */
export function downloadPresetFile(preset, filename) {
  const name =
    filename ||
    `${PRESET_EXPORT_BASENAME}-${new Date().toISOString().slice(0, 10)}.json`;
  const blob = new Blob([serializePreset(preset)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = name;
  anchor.click();
  URL.revokeObjectURL(url);
}

/**
 * @param {string} text
 * @returns {StoredPreset | null}
 */
export function readPresetFromJsonText(text) {
  try {
    return parseStoredPreset(JSON.parse(text));
  } catch {
    return null;
  }
}

/**
 * @param {THREE.Texture} texture
 * @returns {Promise<string>}
 */
export function textureToDataUrl(texture) {
  const img = texture.image;
  if (!img || !img.width || !img.height) {
    return Promise.reject(new Error('Map image is not ready'));
  }

  const canvas = document.createElement('canvas');
  canvas.width = img.width;
  canvas.height = img.height;
  const ctx = canvas.getContext('2d');
  if (!ctx) return Promise.reject(new Error('Could not encode map image'));

  ctx.drawImage(img, 0, 0);

  let dataUrl = '';
  try {
    dataUrl = canvas.toDataURL('image/webp', 0.92);
  } catch {
    dataUrl = canvas.toDataURL('image/png');
  }

  if (!dataUrl.startsWith('data:')) {
    return Promise.reject(new Error('Could not encode map image'));
  }

  return Promise.resolve(dataUrl);
}

/**
 * Save preset to Supabase with async handling.
 * @param {StoredPreset} preset
 * @param {{ includeMap?: boolean }} [options]
 * @returns {Promise<{ savedMap: boolean; warnings: string[] }>}
 */
export async function savePresetWithFallback(preset, options = {}) {
  const includeMap = options.includeMap !== false;

  if (!includeMap) {
    const withoutMap = { ...preset, mapDataUrl: null, mapFileLabel: null };
    const { warnings } = await savePresetToStorage(withoutMap);
    return { savedMap: false, warnings };
  }

  const { warnings } = await savePresetToStorage(preset);
  return { savedMap: Boolean(preset.mapDataUrl), warnings };
}
